import { Option, Result, Enum2 } from "./common";

export type Method = "GET" | "POST" | "PUT" | "DELETE";
//加锁错误处理器
export type LockErrorHandler = () => void;
//请求错误处理器
export type ReqErrorHandler = (err: any) => void;
//业务错误处理器
export type UnwrapErrorHandler = (err: string) => void;
//请求处理器，自己提供ajax实现
export type HttpRequestor = (method: Method, url: string, query: string | void, body: any) => Promise<any>;
//数据解包器，输入请求器返回的结果，输出Result<成功结果，业务错误>
export type DataUnwrapper<T> = (resp: any) => Promise<Result<T, string>>;
//锁处理器，lock参数为true表示加锁，lock参数为false表示解锁，返回true表示执行成功，返回false表示执行失败
export type LockHandler = (lock: boolean) => boolean;
//loading展示器
export type LoadingHandler = (show: boolean) => void;

export interface Encryptor {
    encryptRequest(req: any): any;
    decryptResponse(resp: any): any;
}

const defaultLockHandler: LockHandler = (lock: boolean): boolean => true;
const singleCacheMap: {
    [url: string]: Option<string>;
} = {};
const multiCacheMap: {
    [url: string]: {
        [key: string]: Option<string>;
    };
} = {};

export interface ReqCfg<Resp> {
    httpRequestor?: HttpRequestor;
    dataUnwrapper?: DataUnwrapper<Resp>;
    lockHandler?: LockHandler;
    loadingHandler?: LoadingHandler;
    //加锁错误处理器（只处理加锁失败错误）
    lockErrorHandler?: LockErrorHandler;
    //请求错误处理器（只处理请求通信错误）
    reqErrorHandler?: ReqErrorHandler;
    //业务错误处理器（只处理业务错误，比如返回码不正确的情况）
    unwrapErrorHandler?: UnwrapErrorHandler;
}

export class Request<Req, Resp> {
    static defaultHttpRequestor: HttpRequestor = (method: Method, url: string, query: string | void, body: any): Promise<any> => {
        return Promise.reject("httpRequestor unimplemented");
    };
    static defaultDataUnwrapper: DataUnwrapper<any> = (resp: any): Promise<Result<any, string>> => {
        return Promise.resolve(Result.Err("dataUnwrapper unimplemented"));
    };
    static defaultLoadingHandler: LoadingHandler = (show: boolean): void => { };
    static defaultLockErrorHandler: LockErrorHandler = (): void => {
        console.warn("operation too frequent");
    };
    static defaultReqErrorHandler: ReqErrorHandler = console.error;
    static defaultUnwrapErrorHandler: UnwrapErrorHandler = console.error;

    private url: string;
    private method: Method;
    private httpRequestor: HttpRequestor;
    private dataUnwrapper: DataUnwrapper<any>;
    private lockHandler: LockHandler;
    private showLoading: boolean;
    private loadingHandler: LoadingHandler;
    private lockErrorHandler: LockErrorHandler;
    private reqErrorHandler: ReqErrorHandler;
    private unwrapErrorHandler: UnwrapErrorHandler;
    private useCache: Option<Enum2<void, string>>;
    private reqMapper: (req: Req) => any;
    private respMapper: (resp: Resp) => any;
    private encryptor: Encryptor | undefined;

    constructor(url: string, method?: Method, opt?: ReqCfg<Resp>) {
        this.url = url;
        this.method = method || "POST";
        this.httpRequestor = (opt && opt.httpRequestor) || Request.defaultHttpRequestor;
        this.dataUnwrapper = (opt && opt.dataUnwrapper) || Request.defaultDataUnwrapper;
        this.lockHandler = (opt && opt.lockHandler) || defaultLockHandler;
        this.loadingHandler = (opt && opt.loadingHandler) || Request.defaultLoadingHandler;
        this.showLoading = true;
        this.lockErrorHandler = (opt && opt.lockErrorHandler) || Request.defaultLockErrorHandler;
        this.reqErrorHandler = (opt && opt.reqErrorHandler) || Request.defaultReqErrorHandler;
        this.unwrapErrorHandler = (opt && opt.unwrapErrorHandler) || Request.defaultUnwrapErrorHandler;
        this.reqMapper = (req) => req;
        this.respMapper = (resp) => resp;
        this.useCache = Option.None();
    }
    setHttpRequestor(httpRequestor: HttpRequestor): this {
        this.httpRequestor = httpRequestor;
        return this;
    }
    setDataUnwrapper<R>(dataUnwrapper: DataUnwrapper<R>): Request<Req, R> {
        this.dataUnwrapper = dataUnwrapper;
        return this as any;
    }
    setLockHandler(lockHandler: LockHandler): this {
        this.lockHandler = lockHandler;
        return this;
    }
    setLoadingHandler(loadingHandler: LoadingHandler): this {
        this.loadingHandler = loadingHandler;
        return this;
    }
    disableLoading(): this {
        this.showLoading = false;
        return this;
    }
    setLockErrorHandler(lockErrorHandler: LockErrorHandler): this {
        this.lockErrorHandler = lockErrorHandler;
        return this;
    }
    setReqErrorHandler(reqErrorHandler: ReqErrorHandler): this {
        this.reqErrorHandler = reqErrorHandler;
        return this;
    }
    setUnwrapErrorHandler(unwrapErrorHandler: UnwrapErrorHandler): this {
        this.unwrapErrorHandler = unwrapErrorHandler;
        return this;
    }
    setEncryptor(encryptor: Encryptor): this {
        this.encryptor = encryptor;
        return this;
    }
    cacheByUrl(): this {
        this.useCache = Option.Some(Enum2.First(undefined));
        return this;
    }
    cacheByUrlAndKey(key: string): this {
        this.useCache = Option.Some(Enum2.Second(key));
        return this;
    }
    mapReq<NewReq>(reqMapper: (newReq: NewReq) => Req): Request<NewReq, Resp> {
        const oldReqMapper = this.reqMapper;
        this.reqMapper = ((newReq: NewReq) => {
            return oldReqMapper(reqMapper(newReq));
        }) as any;
        return this as any;
    }
    mapResp<NewResp>(respMapper: (resp: Resp) => NewResp): Request<Req, NewResp> {
        const oldRespMapper = this.respMapper;
        this.respMapper = ((resp: Resp) => {
            return respMapper(oldRespMapper(resp));
        }) as any;
        return this as any;
    }
    private formatQuery(query?: string | {
        [key: string]: any;
    }): string | void {
        if ("object" === typeof query && query) {
            return Object.keys(query).map((key) => {
                const value = encodeURIComponent(query[key]);
                return `${key}=${value}`;
            }).join("&");
        } else {
            return query;
        }
    }
    call(body: Req, query?: string | {
        [key: string]: any;
    }): Promise<Resp> {
        if (!this.lockHandler(true)) {
            //加锁失败，直接返回
            this.lockErrorHandler();
            return Promise.reject("lock failed before requesting");
        }
        this.showLoading && this.loadingHandler(true);
        let respOpt: Option<() => Promise<Resp>> = Option.None();
        this.useCache.match((type) => {
            //可以从缓存里获取
            type.match(() => {
                //按url缓存的
                if (singleCacheMap[this.url]) {
                    respOpt = singleCacheMap[this.url].map((resp) => {
                        return () => {
                            this.lockHandler(false);
                            this.showLoading && this.loadingHandler(false);
                            return Promise.resolve(JSON.parse(resp)[0]);
                        };
                    });
                }
            }, (key: string) => {
                //按url和key缓存的
                const data = multiCacheMap[this.url];
                if (data && data[key]) {
                    respOpt = data[key].map((resp) => {
                        return () => {
                            this.lockHandler(false);
                            this.showLoading && this.loadingHandler(false);
                            return Promise.resolve(JSON.parse(resp)[0]);
                        };
                    });
                }
            });
        });
        return respOpt.unwrapOr(() => {
            const finalBody = this.reqMapper(body);
            const encryptedBody = this.encryptor ? this.encryptor.encryptRequest(finalBody) : finalBody;
            return this.httpRequestor(this.method, this.url, this.formatQuery(query), encryptedBody).then((encryptedResp: any) => {
                const resp = this.encryptor ? this.encryptor.decryptResponse(encryptedResp) : encryptedResp;
                return this.dataUnwrapper(resp).then((result) => {
                    this.showLoading && this.loadingHandler(false);
                    this.lockHandler(false);
                    return new Promise((resolve, reject) => {
                        result.match((originalResp: Resp) => {
                            const resp = this.respMapper(originalResp);
                            //如果使用缓存添加缓存
                            this.useCache.match((type) => {
                                type.match(() => {
                                    singleCacheMap[this.url] = Option.Some(JSON.stringify([resp]));
                                }, (key: string) => {
                                    let data = multiCacheMap[this.url];
                                    if (!data) {
                                        multiCacheMap[this.url] = {};
                                        data = multiCacheMap[this.url];
                                    }
                                    data[key] = Option.Some(JSON.stringify([resp]));
                                });
                            });
                            resolve(resp);
                        }, (bizErr: string) => {
                            this.unwrapErrorHandler(bizErr);
                            reject(bizErr);
                        });
                    });
                }, (err) => {
                    this.showLoading && this.loadingHandler(false);
                    this.lockHandler(false);
                    console.error("unwrap response data failed", err);
                    const friendlyErrMsg = "unwrap response data failed";
                    this.unwrapErrorHandler(friendlyErrMsg);
                    throw friendlyErrMsg;
                });
            }, (reqErr) => {
                this.lockHandler(false);
                this.showLoading && this.loadingHandler(false);
                this.reqErrorHandler(reqErr);
                throw reqErr;
            });
        })();
    }
    get(query?: string | {
        [key: string]: any;
    }): Promise<Resp> {
        this.method = "GET";
        return this.call(null as any, query);
    }
    post(body: Req, query?: string | {
        [key: string]: any;
    }): Promise<Resp> {
        this.method = "POST";
        return this.call(body, query);
    }
}
